import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import client from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Check, Sparkles, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";

const FEATURES_FREE = [
    "15 credits / month",
    "1 project · 5 keywords",
    "AEO content analyzer",
    "Basic PDF reports",
    "3 shareable dashboards",
];

const FEATURES_STARTER = [
    "Everything in Free, plus:",
    "100 credits / month",
    "3 projects · 100 keywords",
    "All AI features (Digest, Brief, Explainer)",
    "Cannibalization detector",
    "Buy credit packs anytime",
];

const FEATURES_PRO = [
    "Everything in Starter, plus:",
    "500 credits / month",
    "10 projects · 500 keywords",
    "Bulk Diagnose (portfolio-wide)",
    "Historical rank tracking",
    "White-label reports + custom logo",
    "Scheduled email reports · 3 team seats",
    "3-day free trial",
];

const FEATURES_AGENCY = [
    "Everything in Pro, plus:",
    "2,000 credits / month",
    "Unlimited projects · 2,500 keywords",
    "Backlinks + Referring Domains",
    "Toxic Link Disavow + Export History",
    "Unlimited shareable dashboards",
    "10 team seats · Priority support",
];

const PLANS = [
    { key: "free",    name: "Free",    price_m: 0,   price_y: 0,    features: FEATURES_FREE,    lookup_m: null,               lookup_y: null,              highlight: false },
    { key: "starter", name: "Starter", price_m: 49,  price_y: 470,  features: FEATURES_STARTER, lookup_m: "starter_monthly",  lookup_y: "starter_yearly",  highlight: false },
    { key: "pro",     name: "Pro",     price_m: 99,  price_y: 950,  features: FEATURES_PRO,     lookup_m: "pro_monthly",      lookup_y: "pro_yearly",      highlight: true, badge: "MOST POPULAR · 3-DAY TRIAL" },
    { key: "agency",  name: "Agency",  price_m: 299, price_y: 2870, features: FEATURES_AGENCY,  lookup_m: "agency_monthly",   lookup_y: "agency_yearly",   highlight: false, badge: "FULL STACK" },
];

export default function Pricing() {
    const { user, loading: authLoading } = useAuth();
    const nav = useNavigate();
    const [loading, setLoading] = useState(null);
    const [interval, setInterval] = useState("month");

    const buy = async (lookupKey) => {
        if (authLoading) return;
        if (!user) { nav("/register"); return; }
        if (!lookupKey) { nav("/app"); return; }
        setLoading(lookupKey);
        try {
            const r = await client.post("/payments/checkout", {
                lookup_key: lookupKey,
                origin_url: window.location.origin,
            });
            window.location.href = r.data.checkout_url;
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Checkout failed");
            setLoading(null);
        }
    };

    return (
        <div className="min-h-screen bg-background text-foreground" data-testid="pricing-page">
            <header className="sticky top-0 z-50 border-b border-border/60 bg-background/70 backdrop-blur-xl">
                <div className="max-w-7xl mx-auto px-6 h-24 flex items-center justify-between">
                    <Link to="/" data-testid="brand-link" className="inline-flex items-center gap-2">
                        <Logo className="h-24 w-auto" />
                    </Link>
                    <div className="flex items-center gap-3 text-sm">
                        {user ? (
                            <Link to="/app" data-testid="nav-app-btn" className="text-muted-foreground hover:text-foreground">Go to app →</Link>
                        ) : (
                            <>
                                <Link to="/login" data-testid="nav-login-btn" className="text-muted-foreground hover:text-foreground">Sign in</Link>
                                <Link to="/register" data-testid="nav-register-btn" className="bg-primary text-primary-foreground px-4 py-2 font-medium">Start free</Link>
                            </>
                        )}
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-6 py-20">
                <div className="max-w-3xl">
                    <div className="overline mb-4">Pricing</div>
                    <h1 className="font-display text-5xl md:text-6xl tracking-tight">
                        Four plans.<br />No gimmicks.
                    </h1>
                    <p className="mt-6 text-muted-foreground text-lg">
                        Free to try. Starter for solo marketers. Pro for agencies. Agency for the heavy hitters. Save 20% yearly.
                    </p>
                    {user && user.credit_months > 0 && (
                        <div className="mt-6 border border-primary/60 bg-primary/5 px-4 py-3 inline-flex items-center gap-3" data-testid="credit-banner">
                            <span className="w-2 h-2 bg-primary rounded-full brand-glow" />
                            <span className="text-sm">
                                You have <span className="text-primary font-mono">{user.credit_months} free month{user.credit_months === 1 ? "" : "s"}</span> from referrals — auto-applied at checkout.
                            </span>
                        </div>
                    )}
                </div>

                <div className="mt-10 flex items-center gap-2">
                    <button
                        data-testid="interval-month"
                        onClick={() => setInterval("month")}
                        className={`px-4 py-2 text-sm border ${interval === "month" ? "border-primary text-primary" : "border-border text-muted-foreground"}`}
                    >Monthly</button>
                    <button
                        data-testid="interval-year"
                        onClick={() => setInterval("year")}
                        className={`px-4 py-2 text-sm border ${interval === "year" ? "border-primary text-primary" : "border-border text-muted-foreground"}`}
                    >Yearly <span className="text-xs text-primary ml-1">save 20%</span></button>
                </div>

                <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {PLANS.map((p) => {
                        const price = interval === "month" ? p.price_m : p.price_y;
                        const lookup = interval === "month" ? p.lookup_m : p.lookup_y;
                        const isCurrent = user?.tier === p.key;
                        return (
                            <div key={p.key}
                                 className={`border p-8 relative ${p.highlight ? "border-primary bg-card/60" : "border-border bg-card/40"}`}
                                 data-testid={`plan-${p.key}`}>
                                {p.badge && (
                                    <div className={`absolute -top-3 left-8 px-2 text-xs font-medium ${p.highlight ? "bg-primary text-primary-foreground" : "bg-card border border-border text-muted-foreground"}`}>
                                        {p.badge}
                                    </div>
                                )}
                                <div className={`overline ${p.highlight ? "text-primary" : ""}`}>{p.name}</div>
                                <div className="mt-4 flex items-baseline gap-2">
                                    <span className={`font-display text-5xl ${p.highlight ? "text-primary" : ""}`}>${price}</span>
                                    <span className="text-muted-foreground text-sm">/ {p.price_m === 0 ? "forever" : interval}</span>
                                </div>
                                <p className="text-sm text-muted-foreground mt-3 min-h-[40px]">
                                    {p.key === "free" && "Everything you need to rank a single site."}
                                    {p.key === "solo" && "Client-ready deliverables for a lean solo marketer."}
                                    {p.key === "agency" && "The full stack — rank history + backlinks + everything else."}
                                </p>
                                <button
                                    data-testid={`cta-${p.key}`}
                                    onClick={() => buy(lookup)}
                                    disabled={loading === lookup || isCurrent}
                                    className={`mt-8 inline-flex items-center justify-center gap-2 w-full py-3 text-sm font-medium disabled:opacity-60 ${
                                        p.highlight
                                            ? "bg-primary text-primary-foreground hover:bg-primary/90"
                                            : "border border-border hover:border-primary"
                                    }`}
                                >
                                    {isCurrent ? (<>Current plan <Sparkles className="w-4 h-4" /></>)
                                        : p.key === "free" ? (user ? "You're on Starter" : "Start free")
                                        : loading === lookup ? "Redirecting to Stripe…"
                                        : (<>Upgrade to {p.name} <ArrowRight className="w-4 h-4" /></>)}
                                </button>
                                <ul className="mt-8 space-y-3">
                                    {p.features.map((f) => (
                                        <li key={f} className="flex items-center gap-3 text-sm">
                                            <Check className={`w-4 h-4 shrink-0 ${p.highlight ? "text-primary" : "text-muted-foreground"}`} />
                                            <span className={p.highlight ? "" : "text-muted-foreground"}>{f}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        );
                    })}
                </div>

            </main>
        </div>
    );
}
